import React, { Component } from "react";
import PropTypes from "prop-types";
import {
  Container,
  Header,
  Segment,
  Icon,
  Divider,
  Tab,
} from "semantic-ui-react";
import { loanApi } from "@api/loans";
import { fineApi } from "@api/fine/fine";
import { withCancel } from "@api/utils";
import { Error } from "@components/Error";
import { Loader } from "@components/Loader";
import { PatronInfo } from "./PatronInfo";
import FineHistoryTable from "./FineHistoryTable";
import { PaymentHistoryService } from "./PaymentHistoryService";

const LOAN_PAGE_SIZE = 1000;
const RETURNED_STATE = "ITEM_RETURNED";
const ACTIVE_STATUS = "active";

export class PaymentHistoryDisplay extends Component {
  constructor(props) {
    super(props);

    this.state = {
      paymentHistory: [],
      fineHistory: [],
      allLoans: [],
      isLoading: true,
      error: {},
      patronInfo: null,
      activeTab: 0,
    };

    this.cancellableFetch = null;
    this.cancellableFinesFetch = null;
  }

  componentDidMount() {
    this.fetchData();
  }

  componentWillUnmount() {
    this.cancelRequests();
  }

  cancelRequests = () => {
    if (this.cancellableFetch) this.cancellableFetch.cancel();
    if (this.cancellableFinesFetch) this.cancellableFinesFetch.cancel();
  };

  fetchData = async () => {
    await Promise.all([this.fetchPaymentHistory(), this.fetchFineHistory()]);
  };

  buildLoanQuery = (patronId, state = null) => {
    const query = loanApi
      .query()
      .withPatronPid(patronId)
      .withSize(LOAN_PAGE_SIZE);
    if (state) query.withState([state]);
    return query.qs();
  };

  fetchAllLoans = async (patronId) => {
    const [allLoansResponse, completedResponse] = await Promise.all([
      this.cancellableFetch.promise,
      loanApi.list(this.buildLoanQuery(patronId, RETURNED_STATE)),
    ]);

    const allLoans = [
      ...allLoansResponse.data.hits,
      ...completedResponse.data.hits,
    ];

    return allLoans.filter(
      (loan, index, self) =>
        index === self.findIndex((l) => l.metadata.pid === loan.metadata.pid)
    );
  };

  filterDisplayableLoans = (loans, overrideData) => {
    return loans.filter((loan, index) => {
      const { metadata } = loan;
      const { pid: loanId, state } = metadata;

      if (state !== RETURNED_STATE) return false;

      const hasOverrideData =
        PaymentHistoryService.getOverrideAmount(overrideData, loanId, index) !==
          null ||
        PaymentHistoryService.isLoanSelectedInOverride(
          overrideData,
          loanId,
          index
        );

      const returnDate = PaymentHistoryService.getActualReturnDate(metadata);
      const dueDate =
        metadata.end_date || metadata.due_date || metadata.dueDate;
      const wasOverdue = PaymentHistoryService.isLoanOverdue(
        dueDate,
        returnDate
      );

      return wasOverdue || hasOverrideData;
    });
  };

  fetchPaymentHistory = async () => {
    const { patronId, overrideData } = this.props;

    if (!patronId) {
      this.setState({
        isLoading: false,
        error: { message: "Patron ID is required" },
      });
      return;
    }

    try {
      this.setState({ isLoading: true });

      this.cancellableFetch = withCancel(
        loanApi.list(this.buildLoanQuery(patronId))
      );

      const uniqueLoans = await this.fetchAllLoans(patronId);
      const loansToDisplay = this.filterDisplayableLoans(
        uniqueLoans,
        overrideData
      );
      const processedHistory = PaymentHistoryService.processPaymentHistory(
        loansToDisplay,
        overrideData
      );

      this.setState({
        paymentHistory: processedHistory,
        allLoans: uniqueLoans,
        patronInfo: uniqueLoans[0]?.metadata.patron || null,
        isLoading: false,
        error: {},
      });
    } catch (error) {
      if (error !== "UNMOUNTED") {
        this.setState({
          isLoading: false,
          error: {
            message: `Failed to fetch payment history: ${
              error.message || error
            }`,
          },
        });
      }
    }
  };

  fetchFineHistory = async () => {
    const { selectedPatron } = this.props;

    if (!selectedPatron?.email) return;

    try {
      this.cancellableFinesFetch = withCancel(
        fineApi.getFinesByUser(selectedPatron.email)
      );
      const fineResponse = await this.cancellableFinesFetch.promise;

      const processedFines = (fineResponse.data.hits || [])
        .filter((fine) => {
          const status =
            fine.status || fine.metadata?.status || fine.metadata?.state;
          return status !== ACTIVE_STATUS;
        })
        .sort((a, b) => {
          const getDate = (fine) =>
            new Date(
              fine.endDate ||
                fine.end_date ||
                fine.startDate ||
                fine.start_date ||
                0
            );
          return getDate(b) - getDate(a);
        });

      this.setState({ fineHistory: processedFines });
    } catch (error) {
      // Silently handle fine fetch errors as it's secondary data
    }
  };

  handleDownloadReceipt = (receiptData, fileName) => {
    PaymentHistoryService.downloadReceipt(receiptData, fileName);
  };

  handleTabChange = (e, { activeIndex }) => {
    this.setState({ activeTab: activeIndex });
  };

  render() {
    const {
      isLoading,
      error,
      patronInfo,
      allLoans,
      paymentHistory,
      fineHistory,
      activeTab,
    } = this.state;
    const { overrideData } = this.props;

    const panes = [
      {
        render: () => (
          <Segment>
            <Header as="h3">
              <Icon name="money bill alternate" />
              Fine Management History
            </Header>
            <FineHistoryTable fineHistory={fineHistory} />
          </Segment>
        ),
      },
    ];

    return (
      <Container fluid>
        <Loader isLoading={isLoading}>
          <Error error={error}>
            <PatronInfo
              patronInfo={patronInfo}
              allLoansCount={allLoans.length}
              overdueCount={paymentHistory.length}
              fineCount={fineHistory.length}
              overrideData={overrideData}
            />

            <Divider />

            <Tab
              panes={panes}
              activeIndex={activeTab}
              onTabChange={this.handleTabChange}
            />
          </Error>
        </Loader>
      </Container>
    );
  }
}

PaymentHistoryDisplay.propTypes = {
  patronId: PropTypes.string.isRequired,
  selectedPatron: PropTypes.object,
  overrideData: PropTypes.object,
};

PaymentHistoryDisplay.defaultProps = {
  selectedPatron: null,
  overrideData: null,
};

export default PaymentHistoryDisplay;
